import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { LogIn, Mail, Lock, ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import LoadingTransition from "../components/ui/LoadingTransition";
import Skeleton from "../components/ui/Skeleton";
import { Eye, EyeOff } from "lucide-react";

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Minimum 6 characters")
    .required("Password is required"),
});

const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
});

function Login() {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login, forgotPassword, isLoading } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(
      showForgotPassword ? forgotPasswordSchema : loginSchema
    ),
  });

  const loginMutation = useMutation({
    mutationFn: ({ email, password }) => login(email, password),
    onSuccess: (data) => {
      if (!data.error && data.success) {
        navigate("/");
      } else {
        toast.error("Invalid email or password");
      }
    },
    onError: () => {
      toast.error("Failed to log in");
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email) => forgotPassword(email),
    onSuccess: (data, variables, context) => {

      if (!data.error) {
        setResetSent(true);
        toast.success(data.message || "Reset link sent to email");
        navigate(`/verify-otp?token=${data?.data?.token || ""}`);
      } else {
        toast.error(data.error || "Failed to send reset email");
      }
    },
    onError: (err) => {
      // `err` is whatever you `throw`d in your API wrapper
      console.error("Reset failed:", err);
      toast.error(err.message || "Failed to send reset email");
    },
  });

  const onSubmitLogin = ({ email, password }) => {
    loginMutation.mutate({ email, password });
  };

  const onSubmitReset = ({ email }) => {
    resetPasswordMutation.mutate(email);
  };

  return (
    <>
      {isLoading ? (
        <LoadingTransition isLoading={isLoading} skeleton={<Skeleton.Card/>} transition={0.9} />
      ) : (
        <div className="flex min-h-screen bg-gray-50">
          <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
            <div className="mx-auto w-full max-w-sm lg:w-96">
              <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
                {showForgotPassword
                  ? "Reset Your Password"
                  : "Admin Panel Login"}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {showForgotPassword
                  ? "Enter your email to receive a password reset link"
                  : "Sign in to your account"}
              </p>

              <div className="mt-8">
                {resetSent && (
                  <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md text-sm">
                    Password reset link has been sent to your email.
                  </div>
                )}

                <form
                  className="space-y-6"
                  onSubmit={handleSubmit(
                    showForgotPassword ? onSubmitReset : onSubmitLogin
                  )}
                >
                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email address
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        {...register("email")}
                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="admin@example.com"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Password - only for login */}
                  {!showForgotPassword && (
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Password
                      </label>
                      <div className="mt-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          {...register("password")}
                          className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <Eye /> : <EyeOff />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.password.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      disabled={
                        loginMutation.isLoading ||
                        resetPasswordMutation.isLoading
                      }
                      className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      {showForgotPassword ? (
                        resetPasswordMutation.isLoading ? (
                          "Sending..."
                        ) : (
                          "Send OTP"
                        )
                      ) : loginMutation.isLoading ? (
                        "Logging in..."
                      ) : (
                        <>
                          <LogIn className="mr-2 h-4 w-4" />
                          Log in
                        </>
                      )}
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="text-center text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(!showForgotPassword);
                        reset();
                        setResetSent(false);
                      }}
                      className="text-primary hover:text-primary-700"
                    >
                      {showForgotPassword
                        ? "Back to login"
                        : "Forgot your password?"}
                    </button>
                  </div>

                  {/* Demo Button */}
                  {/* {!showForgotPassword && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setValue("email", "admin@example.com");
                      setValue("password", "password");
                    }}
                    className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Demo Credentials
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </div>
              )} */}
                </form>
              </div>
            </div>
          </div>

          {/* Side Graphic */}
          <div className="hidden lg:block relative w-0 flex-1 bg-gradient-to-r from-primary-600 to-primary-800">
            <div className="absolute inset-0 flex flex-col justify-center items-center p-12 text-white">
              <h1 className="text-4xl font-bold mb-4 text-center">
                Pet Virtual Assistant
                <br />
                Admin Panel
              </h1>
              <p className="text-lg max-w-md text-center text-indigo-100">
                Manage your pet virtual assistant app, users, subscriptions, and
                more from this centralized dashboard.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Login;
